import { cn } from '@chopchop-se/ui';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import type { NavSection } from '../app/nav';

/**
 * The admin sidebar. Sections come from the area registry, already filtered by
 * permission (`buildNavSections`) — this component only draws them.
 *
 * Below `lg` it becomes an off-canvas drawer: the warehouse office runs 27"
 * screens, but stocktaking and receiving happen on 10" tablets, and a fixed
 * 240px rail eats a third of that.
 */
export function Sidebar({
  sections,
  open,
  onClose,
}: {
  sections: readonly NavSection[];
  open: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation(['common', 'shell']);

  return (
    <>
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-ink/20 backdrop-blur-[2px] lg:hidden"
          aria-label={t('shell:layout.closeMenu')}
          onClick={onClose}
        />
      ) : null}

      <nav
        aria-label={t('shell:layout.mainNavigation')}
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 shrink-0 overflow-y-auto border-r border-surface-border bg-surface px-3 py-4 transition-transform scrollbar-thin',
          'lg:sticky lg:top-0 lg:z-0 lg:max-h-[calc(100vh-4rem)] lg:translate-x-0 lg:bg-transparent lg:pt-6',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-3 flex items-center justify-end lg:hidden">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('shell:layout.closeMenu')}
            className="rounded-full p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-5">
          {sections.map((section) => (
            <div key={section.id}>
              {section.labelKey ? (
                <p className="mb-1.5 px-2 font-mono text-[0.6875rem] font-medium uppercase tracking-[0.16em] text-ink-faint">
                  {t(section.labelKey)}
                </p>
              ) : null}
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end ?? false}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-brand-600/[0.10] font-medium text-brand-700'
                            : 'text-ink-light hover:bg-brand-600/[0.06] hover:text-ink',
                        )
                      }
                    >
                      {item.icon ? <item.icon className="size-4 shrink-0" aria-hidden /> : null}
                      <span className="truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
